import { Box, Button, Stack, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { FC, PropsWithChildren, ReactNode } from 'react'
import { BRAND_CORAL, BRAND_CORAL_WASH } from '../constants'

export const StageHeading: FC<{ title: string; blurb?: string }> = ({
    title,
    blurb,
}) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {title}
        </Typography>
        {blurb && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {blurb}
            </Typography>
        )}
    </Box>
)

export const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
    <Typography
        variant="overline"
        sx={{
            display: 'block',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: 'text.secondary',
            mt: 4,
            // The field below floats its label up above its own border on focus, so
            // a tight margin here makes the two labels collide. Leave room for it.
            mb: 2.5,
        }}
    >
        {children}
    </Typography>
)

export const FieldGrid: FC<PropsWithChildren> = ({ children }) => (
    <Box
        sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        }}
    >
        {children}
    </Box>
)

/** Shared TextField styling so every input on the page focuses identically. */
export const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 1.5,
        backgroundColor: '#fff',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND_CORAL,
            borderWidth: 2,
        },
        '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${BRAND_CORAL_WASH}`,
        },
    },
    '& label.Mui-focused': { color: BRAND_CORAL },
}

interface NavButtonsProps {
    onBack?: () => void
    onNext?: () => void
    nextLabel?: string
    backLabel?: string
    isSubmitting?: boolean
    nextDisabled?: boolean
}

export const NavButtons: FC<NavButtonsProps> = ({
    onBack,
    onNext,
    nextLabel = 'Continue',
    backLabel = 'Back',
    isSubmitting = false,
    nextDisabled = false,
}) => (
    <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 4, justifyContent: 'space-between' }}
    >
        <Box>
            {onBack && (
                <Button
                    onClick={onBack}
                    startIcon={<ArrowBackIcon />}
                    disabled={isSubmitting}
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                    {backLabel}
                </Button>
            )}
        </Box>
        {onNext && (
            <Button
                onClick={onNext}
                variant="contained"
                endIcon={!isSubmitting && <ArrowForwardIcon />}
                disabled={isSubmitting || nextDisabled}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 1.5,
                    backgroundColor: BRAND_CORAL,
                    '&:hover': { backgroundColor: '#f06147' },
                }}
            >
                {isSubmitting ? 'Submitting…' : nextLabel}
            </Button>
        )}
    </Stack>
)
